import { ArrowRight, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_BG =
    "https://images.unsplash.com/photo-1711560705654-325ba859c38b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxkZWVwJTIwc3BhY2UlMjBuZWJ1bGF8ZW58MHx8fHwxNzgxMzQyMzU3fDA&ixlib=rb-4.1.0&q=85";
const PUMP_URL =
    "https://pump.fun/coin/4dAwbA1GxBoEMrJPrynPnGViQcJW1NULVB1rTNzbpump";

export default function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-screen w-full overflow-hidden flex items-center"
        >
            {/* Background image */}
            <div
                aria-hidden
                className="absolute inset-0 bg-cover bg-center scale-110"
                style={{ backgroundImage: `url(${HERO_BG})` }}
            />
            {/* Dark overlay */}
            <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-b from-[#030014]/50 via-[#030014]/80 to-[#030014]"
            />
            {/* Star overlay + grid */}
            <div aria-hidden className="absolute inset-0 stars-bg opacity-40" />
            <div
                aria-hidden
                className="absolute inset-0 grid-texture opacity-50 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]"
            />

            {/* Glow blobs */}
            <div
                aria-hidden
                className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-[#00F0FF]/15 blur-[120px]"
            />
            <div
                aria-hidden
                className="absolute -bottom-40 right-0 w-[600px] h-[600px] rounded-full bg-[#B026FF]/20 blur-[140px]"
            />

            <div className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pt-32 pb-24">
                <div className="grid lg:grid-cols-12 gap-12 items-center">
                    {/* LEFT */}
                    <div className="lg:col-span-8 animate-fade-up">
                        {/* Eyebrow */}
                        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-md px-4 py-2 mb-8">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00F0FF] opacity-75" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00F0FF]" />
                            </span>
                            <span
                                className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-slate-300"
                                data-testid="hero-status"
                            >
                                Live on Solana · Pump.fun
                            </span>
                        </div>

                        {/* Title */}
                        <h1
                            data-testid="hero-title"
                            className="font-display text-4xl sm:text-6xl lg:text-[5.5rem] leading-[0.95] font-black tracking-tighter uppercase text-white"
                        >
                            SPACE_100X:
                            <br />
                            <span className="gradient-text">
                                THE NEXT QUANTUM
                            </span>
                            <br />
                            <span className="text-white">LEAP IN </span>
                            <span className="text-[#00F0FF] text-glow-blue cursor-blink">
                                CRYPTO
                            </span>
                        </h1>

                        {/* Subhead */}
                        <p
                            data-testid="hero-subtitle"
                            className="mt-8 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed font-mono"
                        >
                            Launch your portfolio to orbit. The ultimate
                            space-themed memecoin on Solana.
                            <span className="text-white">
                                {" "}
                                Join the mission to the moon.
                            </span>
                        </p>

                        {/* CTAs */}
                        <div className="mt-10 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                            <a
                                href={PUMP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-buy-button"
                            >
                                <Button className="group relative h-14 px-8 rounded-full bg-[#00F0FF] hover:bg-[#00F0FF] text-black font-display font-black uppercase tracking-tight text-sm sm:text-base animate-glow transition-transform hover:-translate-y-0.5">
                                    <Rocket
                                        size={18}
                                        className="mr-2 group-hover:translate-x-1 transition-transform"
                                    />
                                    Buy on Pump.fun
                                    <ArrowRight
                                        size={18}
                                        className="ml-2 group-hover:translate-x-1 transition-transform"
                                    />
                                </Button>
                            </a>

                            <a
                                href="#tokenomics"
                                data-testid="hero-tokenomics-link"
                                className="inline-flex items-center gap-2 h-14 px-6 rounded-full border border-white/15 bg-white/[0.03] backdrop-blur text-white font-mono text-sm uppercase tracking-[0.2em] hover:border-[#B026FF]/60 hover:text-[#B026FF] transition-colors"
                            >
                                View Tokenomics
                            </a>
                        </div>

                        {/* Stats strip */}
                        <div className="mt-14 grid grid-cols-3 gap-4 sm:gap-8 max-w-xl">
                            {[
                                { k: "Chain", v: "Solana" },
                                { k: "Launch", v: "Fair" },
                                { k: "Mission", v: "Moon" },
                            ].map((s) => (
                                <div
                                    key={s.k}
                                    data-testid={`hero-stat-${s.k.toLowerCase()}`}
                                    className="border-l border-white/10 pl-3 sm:pl-5"
                                >
                                    <div className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.25em] text-slate-400">
                                        {s.k}
                                    </div>
                                    <div className="font-display text-lg sm:text-2xl font-black text-white mt-1">
                                        {s.v}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* RIGHT: Orbital ring */}
                    <div className="lg:col-span-4 hidden lg:flex justify-center">
                        <div className="relative w-[360px] h-[360px] animate-float">
                            <div className="absolute inset-0 rounded-full neon-border-purple animate-[orbit_22s_linear_infinite]" />
                            <div className="absolute inset-8 rounded-full neon-border-blue animate-[orbit_14s_linear_infinite_reverse]" />
                            <div className="absolute inset-16 rounded-full bg-gradient-to-br from-[#00F0FF]/40 via-[#B026FF]/40 to-transparent blur-2xl" />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-40 h-40 rounded-full bg-[#0B0B1A] border border-white/10 flex flex-col items-center justify-center shadow-[0_0_60px_rgba(0,240,255,0.35)]">
                                    <span className="font-mono text-[10px] tracking-[0.3em] text-slate-400 uppercase">
                                        Target
                                    </span>
                                    <span className="font-display text-3xl font-black text-white mt-1">
                                        100×
                                    </span>
                                    <span className="font-mono text-[10px] tracking-[0.2em] text-[#00F0FF] mt-1">
                                        ▲ TO THE MOON
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom marquee */}
            <div
                aria-hidden
                className="absolute bottom-0 left-0 right-0 border-y border-white/10 bg-black/60 backdrop-blur-md overflow-hidden"
            >
                <div className="marquee py-3">
                    {Array.from({ length: 12 }).map((_, i) => (
                        <div
                            key={i}
                            className="flex items-center gap-10 px-10 font-mono text-xs uppercase tracking-[0.3em] text-slate-300 whitespace-nowrap"
                        >
                            <span>◇ Mission to the Moon</span>
                            <span className="text-[#00F0FF]">★ 100× Loading</span>
                            <span>◇ Solana Speed</span>
                            <span className="text-[#B026FF]">★ Community Owned</span>
                            <span>◇ Fair Launch</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
